import React from 'react';
import { Box, Avatar, Typography, IconButton } from '@mui/material';
import XIcon from '@mui/icons-material/X';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

/**
 * Componente compacto que muestra autor + redes sociales + botones de compartir + botón de comentarios
 */
const NoticiaAutorYCompartir = ({ author, shareUrl, title, commentsCount, onOpenComments }) => {
    const getTwitterHandle = () => {
        if (!author?.twitter) return null;
        // Remover @ si está presente
        return author.twitter.replace('@', '');
    };

    const twitterHandle = getTwitterHandle();

    const handleTwitterShare = () => {
        const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
        window.open(url, '_blank', 'width=600,height=400');
    };

    const handleWhatsAppShare = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(title + ' ' + shareUrl)}`;
        window.open(url, '_blank');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                my: 1,
            }}
        >
            {/* Autor */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                    src={author?.image}
                    alt={author?.name}
                    sx={{
                        width: 50,
                        height: 50,
                    }}
                />
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {author?.name}
                    </Typography>
                    {/* Redes sociales del autor */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {twitterHandle && (
                            <IconButton
                                size="small"
                                href={`https://x.com/${twitterHandle}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    p: 0.25,
                                    bgcolor: '#000000',
                                    color: 'white',
                                    width: 20,
                                    height: 20,
                                    '&:hover': {
                                        bgcolor: '#333333',
                                    },
                                }}
                            >
                                <XIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        )}
                        {author?.socialNetworks?.map((network, index) => {
                            if (
                                network.name?.toLowerCase() === 'youtube' ||
                                network.name?.toLowerCase() === 'tiktok'
                            ) {
                                return (
                                    <IconButton
                                        key={index}
                                        size="small"
                                        href={network.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            p: 0.25,
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            width: 20,
                                            height: 20,
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                            },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 10 }}>
                                            {network.name?.[0]?.toUpperCase()}
                                        </Typography>
                                    </IconButton>
                                );
                            }
                            return null;
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Botones de compartir + comentarios */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Twitter/X */}
                <IconButton
                    onClick={handleTwitterShare}
                    sx={{
                        bgcolor: '#000000',
                        color: 'white',
                        width: 20,
                        height: 20,
                        '&:hover': {
                            bgcolor: '#333333',
                        },
                    }}
                >
                    <XIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* WhatsApp */}
                <IconButton
                    onClick={handleWhatsAppShare}
                    sx={{
                        bgcolor: '#25D366',
                        color: 'white',
                        width: 20,
                        height: 20,
                        '&:hover': {
                            bgcolor: '#128C7E',
                        },
                    }}
                >
                    <WhatsAppIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Botón de comentarios */}
                <Box
                    onClick={onOpenComments}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        border: 1,
                        borderColor: 'primary.main',
                        borderRadius: '24px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: 'background.darkCard',
                        position: 'relative',
                        boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.5)',
                        '@keyframes bounceAttention': {
                            '0%, 100%': {
                                transform: 'translateY(0)',
                            },
                            '10%': {
                                transform: 'translateY(-8px)',
                            },
                            '20%': {
                                transform: 'translateY(0)',
                            },
                            '30%': {
                                transform: 'translateY(-4px)',
                            },
                            '40%': {
                                transform: 'translateY(0)',
                            },
                        },
                        '@keyframes pulseGlow': {
                            '0%, 100%': {
                                boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.5)',
                            },
                            '50%': {
                                boxShadow: '0 0 12px 4px rgba(239, 68, 68, 0.3)',
                            },
                        },
                        animation:
                            'bounceAttention 3s ease-in-out infinite, pulseGlow 3s ease-in-out infinite',
                        '&:hover': {
                            transform: 'translateY(-2px) !important',
                            boxShadow: '0 0 16px 4px rgba(239, 68, 68, 0.4)',
                            animation: 'none',
                        },
                    }}
                >
                    <ChatBubbleOutlineIcon
                        sx={{
                            fontSize: 20,
                            color: 'primary.main',
                        }}
                    />
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {commentsCount}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default NoticiaAutorYCompartir;
